import { Directive, inject, OnDestroy, OnInit } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { ActivatedRoute, ActivatedRouteSnapshot, NavigationEnd, Router } from '@angular/router';
import { Subject, Subscription } from 'rxjs';
import { distinctUntilChanged, filter } from 'rxjs/operators';

import { relatedResources } from '../../components/layouts/form-view/form-view.component';
import { speedDialButtons } from '../../components/layouts/speed-dial-fab/speed-dial-fab.component';
import { ApiResponseModel } from '../../models/core/api-response.model';
import { ActionButtonsService } from '../../services/action-buttons.service';
import { ErrorsService } from '../../services/errors.service';
import { NotificationService } from '../../services/notification.service';
import { Tools } from '../utils/tools';
import { BaseEntityModel } from './base-entity.model';
import { BaseEntityResource } from './base-entity.resource';
import { BaseComponent } from './base.component';

import Utils = Tools.Utils;

@Directive()
export abstract class BaseFormComponent<
        T extends BaseEntityModel,
        TSummary extends BaseEntityModel = T,
        TListEntityModel extends BaseEntityModel = T
    >
    extends BaseComponent
    implements OnInit, OnDestroy
{
    record?: T;
    recordSummary?: TSummary;
    recordId?: string;
    isEdit = false;
    isView = false;
    isCreate = false;
    isLoading = true;
    loadingStatusChange = new Subject<boolean>();
    urlChange = new Subject<void>();
    formGroup: FormGroup;

    private errorSubscription?: Subscription;
    private useBaseEntityForSummary = false;
    private readonly router = inject(Router);
    private readonly activatedRoute = inject(ActivatedRoute);
    private readonly errorsService = inject(ErrorsService);
    private readonly notificationService = inject(NotificationService);
    private readonly actionButtonsService = inject(ActionButtonsService);

    protected abstract readonly _entityResource: BaseEntityResource<T, TSummary, TListEntityModel>;

    ngOnInit(): void {
        this.setupInitialData(this.activatedRoute.snapshot);

        this.errorsService.shouldDisplayAlertOnError = false;
        const routerSub = this.router.events
            .pipe(
                filter(event => event instanceof NavigationEnd),
                distinctUntilChanged()
            )
            .subscribe(() => {
                this.changeLoadingStatus(true);
                this.setupInitialData(this.activatedRoute.snapshot);
                this.urlChange.next();
            });

        this.addSubscriptions(routerSub);
    }

    override ngOnDestroy(): void {
        super.ngOnDestroy();

        if (!!this.errorSubscription) {
            this.errorSubscription.unsubscribe();
        }

        this.errorsService.shouldDisplayAlertOnError = true;
    }

    save(): void {
        if (this.isView) {
            return;
        }

        this.record = this.record || ({} as T);
        Object.assign(this.record, this.formGroup.value);

        if (this.isEdit) {
            this._entityResource.edit(this.record).subscribe(r => {
                if (!!r.value) {
                    this.notificationService.showSuccess('General.SuccessfullySaved');
                    this.gotToView();
                }
            });
        } else {
            this._entityResource.add(this.record).subscribe(r => {
                if (!!r.value) {
                    this.notificationService.showSuccess('General.SuccessfullySaved');
                    this.recordId = r.value.result.toString();
                    this.gotToView();
                }
            });
        }
    }

    gotToView(): void {
        this.router.navigate([this.recordId], { relativeTo: this.activatedRoute.parent }).then();
    }

    private changeLoadingStatus(isLoading: boolean): void {
        this.isLoading = isLoading;
        this.loadingStatusChange.next(isLoading);
    }

    private resetFormGroup(): void {
        if (!!this.formGroup) {
            this.formGroup.patchValue(this.record ?? {});
            this.formGroup.markAsPristine();
        }
    }

    private setupInitialData(snapshot: ActivatedRouteSnapshot): void {
        this.changeLoadingStatus(true);

        this.recordId = snapshot.paramMap.get('id') ?? undefined;
        this.isEdit = snapshot.url.findIndex(r => r.path === 'edit') >= 0;
        this.isView = !!this.recordId && !this.isEdit;
        this.isCreate = !(this.isEdit || this.isView);

        if (!this.isView) {
            this.errorSubscription = this.errorsService.onBusinessException.subscribe(r =>
                Utils.displayBusinessExceptions(this.formGroup, r, false)
            );
        } else if (!!this.errorSubscription) {
            this.errorSubscription.unsubscribe();
        }

        if (this.isEdit || (this.isView && this.useBaseEntityForSummary)) {
            this._entityResource.get(this.recordId!).subscribe({
                next: r => {
                    this.record = r.value;
                    this.loadRecord(r);
                },
                error: () => this.changeLoadingStatus(false)
            });
        } else if (this.isView && !this.useBaseEntityForSummary) {
            this._entityResource.getSummary(this.recordId!).subscribe({
                next: r => {
                    this.recordSummary = r.value;
                    this.loadRecord(r);
                },
                error: () => this.changeLoadingStatus(false)
            });
        } else {
            this.changeLoadingStatus(false);
        }
    }

    private loadRecord(r: ApiResponseModel<any>): void {
        if (!!r.relatedActions) {
            speedDialButtons.set(this.actionButtonsService.convert(r.relatedActions));
        }

        if (!!r.relatedResources) {
            relatedResources.set(r.relatedResources);
        }

        this.resetFormGroup();
        this.changeLoadingStatus(false);
    }
}
